import {
  getSafeUrlParametersFromWindow,
  LIST_EXTENSION,
} from '@thg-commerce/enterprise-core'
import {
  ProductList,
  ProductSort,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ProductCategoryItemProps } from '@thg-commerce/enterprise-widget-product-categories'
import { ChangeType } from '@thg-commerce/enterprise-widget-product-list-page'
import { getFacetInputFromUrl } from '@thg-commerce/enterprise-widgets/src/ProductListWidgetRenderer/utils'

export const sortOrderOptions = (sortOrder, experiments, defaultSortOrder) => {
  const experimentSortOrder = {
    'v1|default_sort_relevance': ProductSort.Relevance,
    'v2|default_sort_popularity': ProductSort.Popularity,
  }[experiments?.['web_default_sort']]

  const validDefaultSortOrder =
    defaultSortOrder &&
    Object.values(ProductSort).includes(defaultSortOrder as ProductSort)

  return (
    sortOrder ||
    (validDefaultSortOrder ? defaultSortOrder : undefined) ||
    experimentSortOrder ||
    ProductSort.Relevance
  )
}

export const sortTypeTexts = (i18nHelper) => {
  return [
    {
      type: ProductSort.Relevance,
      displayText: i18nHelper('product.list.sort.relevance'),
    },
    {
      type: ProductSort.Popularity,
      displayText: i18nHelper('product.list.sort.popularity'),
    },
    {
      type: ProductSort.PriceLowToHigh,
      displayText: i18nHelper('product.list.sort.pricelowtohigh'),
    },
    {
      type: ProductSort.PriceHighToLow,
      displayText: i18nHelper('product.list.sort.pricehightolow'),
    },
    {
      type: ProductSort.NameAlphabetically,
      displayText: i18nHelper('product.list.sort.namealphabetically'),
    },
    {
      type: ProductSort.NewestToOldest,
      displayText: i18nHelper('product.list.sort.newesttooldest'),
    },
    {
      type: ProductSort.DiscountPercentageHighToLow,
      displayText: i18nHelper('product.list.sort.discountpercentagehightolow'),
    },
  ]
}

export const sortTypesOptions = (experiments, i18nHelper, defaultSortOrder) => {
  const isValidProductSort = (value) =>
    Object.values(ProductSort).includes(value)

  const defaultSort =
    (isValidProductSort(defaultSortOrder) ? defaultSortOrder : undefined) ||
    {
      'v1|default_sort_relevance': ProductSort.Relevance,
      'v2|default_sort_popularity': ProductSort.Popularity,
    }[experiments?.['web_default_sort']] ||
    ProductSort.Relevance

  let options = sortTypeTexts(i18nHelper)

  const defaultSortItem = options.find(({ type }) => type === defaultSort) || {
    type: defaultSort,
    displayText: i18nHelper(`product.list.sort.${defaultSort.toLowerCase()}`),
  }

  options = [
    defaultSortItem,
    ...options.filter(({ type }) => type !== defaultSort),
  ]

  return options
}

export const processedProductList = (data) =>
  (data?.productListProducts?.page?.widgets
    ?.filter((widget) => widget['__typename'] === 'ProductListWidget')
    .pop() as unknown) as {
    description: string | null
    seoDescription: string | null
    products: ProductList
  }

export const breadcrumbs = (breadcrumbsData) =>
  breadcrumbsData.map((item) => {
    return {
      text: item.displayName,
      url:
        item.pagePath === '/'
          ? item.pagePath
          : item.pagePath.concat(LIST_EXTENSION),
    }
  })

export const breadcrumbsSchema = (breadcrumbsData, originUrl) =>
  breadcrumbsData.map((breadcrumb, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.text,
      item: originUrl.concat(breadcrumb.url),
    }
  })

export const topProductCategorySet = (data) =>
  data.page?.widgets?.find(
    (item) => item['__typename'] === 'TopProductCategorySet',
  ) as any

export const productCategoryItems = (data) =>
  (topProductCategorySet(data)
    ?.banners?.filter(
      (item) => item.topProductCategoryName && item.topProductCategoryUrl,
    )
    .map((item) => {
      return {
        text: item.topProductCategoryName as string,
        href: item.topProductCategoryUrl as string,
      }
    }) || []) as ProductCategoryItemProps[]

export const processedProducts = (data) =>
  (processedProductList(data)?.products &&
    processedProductList(data).products.products) ||
  []

export const processedProductListData = (
  data,
  setProductList,
  productListData,
  productItemsPerPage,
  sortOptions,
) => {
  const url = new URL(window.location.href)
  setProductList({
    // TODO: conditionally merge when products exist
    products: processedProducts(data),
    itemsPerPage: productItemsPerPage,
    title: productListData.productList?.title || '',
    description: productListData.productList?.description || '',
    seoDescription: productListData.productList?.seoDescription || '',
    changeType: ChangeType.NONE,
    categories: productCategoryItems(data),
    categoriesTitle: topProductCategorySet(data)?.DiscoverTitle || null,
    facets: processedProductList(data)?.products?.facets,
    pageOptions: {
      sort: sortOptions,
      facets: getFacetInputFromUrl(url.searchParams.get('facetFilters') || ''),
      pageNumber:
        getSafeUrlParametersFromWindow<{ pageNumber: string }>(
          window,
          'pageNumber',
        ).pageNumber || 1,
    },
    totalItems:
      processedProductList(data)?.products?.total ||
      processedProducts(data).length ||
      0,
  })
}

export const getTagKeys = (contentKeys, tagKeys) => {
  if (!contentKeys || !tagKeys) return []

  return contentKeys.filter((contentKey) => tagKeys.includes(contentKey))
}
